import { retry, type BaseQueryFn, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../flatbuffers/api/flatBufferBaseQuery";
import { withScopeRefusalMessage } from "../utils/serverScopeRefusal";
import { isUnreadableReply, retryRateLimited, retryUnlessUnreadable } from "./retryConditions";

/** How many times an API sends a failed request again, unless it sets its own number. */
export const DEFAULT_MAX_RETRIES = 2;

/** The longest a rate-limited attempt waits for the `Retry-After` the server named. */
export const MAX_RETRY_AFTER_MS = 10_000;

/** How long a rate-limited attempt waits when the server named no usable `Retry-After`. */
export const DEFAULT_RETRY_AFTER_MS = 1_000;

/**
 * The wait a 429 asks for, read from the `Retry-After` of the response in a
 * base query's `meta`: a number of seconds (fractions allowed), capped at
 * `MAX_RETRY_AFTER_MS`. A missing response or header, or a value that is not a
 * number of seconds (an HTTP date included), waits `DEFAULT_RETRY_AFTER_MS`.
 * The browser shows the header only when the server's CORS exposes it.
 */
function retryAfterMs(meta: unknown): number {
  const response =
    typeof meta === "object" && meta !== null && "response" in meta
      ? (meta as { response?: unknown }).response
      : undefined;
  if (!(response instanceof Response)) return DEFAULT_RETRY_AFTER_MS;
  const header = response.headers.get("Retry-After");
  const seconds = header === null || header.trim() === "" ? Number.NaN : Number(header);
  if (!Number.isFinite(seconds) || seconds < 0) return DEFAULT_RETRY_AFTER_MS;
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
}

/** True when a base query's result is a 429 from the server's rate limiter. */
function isRateLimited(result: { error?: unknown }): boolean {
  const { error } = result;
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: unknown }).status === 429
  );
}

/**
 * Builds the condition an API's retries follow from its retry budget:
 * `retryUnlessUnreadable` (the default) or `retryUnlessClientError`.
 */
export type RetryRule = typeof retryUnlessUnreadable;

/**
 * A base query that reports its failures as FetchBaseQueryErrors, as
 * fetchBaseQuery and the FlatBuffer base query do. `eachAttempt` adds one of
 * its own, so a base query with another error type must not get in.
 */
type FetchErrorBaseQuery = BaseQueryFn &
  BaseQueryFn<never, unknown, FetchBaseQueryError, never, unknown>;

/** A retry condition as an endpoint's `extraOptions.retryCondition` carries one. */
type RetryCondition = (error: FetchBaseQueryError, args: unknown, extraArgs: unknown) => boolean;

/** The attempts behind each error `withRetries` returned (see `attemptsMade`). */
const attemptsByError = new WeakMap<object, number>();

/**
 * How many times the request that ended in `error` was attempted (the first
 * send and every retry), or undefined for an error `withRetries` did not
 * return. RTK Query hands the base query's error object to the store and the
 * hooks unchanged, so a hook's `error` finds its count here.
 */
export function attemptsMade(error: unknown): number | undefined {
  return typeof error === "object" && error !== null ? attemptsByError.get(error) : undefined;
}

/**
 * One attempt of a request, as `retry` makes them: counted against the
 * request's own options object (which `retry` hands to every attempt), and
 * with an error the base query THROWS returned as a CUSTOM_ERROR.
 *
 * RTK Query's `retry` (1.9) gives up on a returned error once the retry rule
 * says so, but retries a thrown one for ever. fetchBaseQuery builds its
 * Request outside its own try block, so an unusable API address (a mistyped
 * port, say) is thrown, and without this the request would spin with no end
 * and no message. An abort is unaffected: fetchBaseQuery returns it as a
 * FETCH_ERROR.
 *
 * The wrapper takes and returns exactly what `baseQuery` does, plus a
 * FetchBaseQueryError, which `FetchErrorBaseQuery` guarantees is the base
 * query's own error type, hence the one assertion: TypeScript cannot spell
 * "the same function type".
 */
function eachAttempt<BaseQuery extends FetchErrorBaseQuery>(
  baseQuery: BaseQuery,
  attempts: WeakMap<object, number>,
): BaseQuery {
  const attempt: BaseQueryFn<unknown, unknown, unknown> = async (args, api, extraOptions) => {
    if (typeof extraOptions === "object" && extraOptions !== null) {
      attempts.set(extraOptions, (attempts.get(extraOptions) ?? 0) + 1);
    }
    try {
      const result = await baseQuery(args, api, extraOptions);
      // The server names in Retry-After how long its limiter needs; RTK's own
      // backoff knows nothing of it and is far shorter, so the attempt waits it
      // out before reporting the 429. The last attempt waits too, which delays
      // the final error by at most MAX_RETRY_AFTER_MS.
      if (isRateLimited(result)) {
        const waitMs = retryAfterMs(result.meta);
        await new Promise<void>((resolve) => {
          setTimeout(resolve, waitMs);
        });
      }
      return result.error
        ? { error: withScopeRefusalMessage(result.error), meta: result.meta }
        : result;
    } catch (thrown) {
      const error: FetchBaseQueryError = { status: "CUSTOM_ERROR", error: String(thrown) };
      return { error };
    }
  };
  return attempt as BaseQuery;
}

/**
 * The options one request runs with: a copy of the endpoint's, so its
 * attempts can be counted against it, and with any `retryCondition` of the
 * endpoint's own made to refuse an unreadable reply first. RTK Query lets an
 * endpoint's condition replace the API's rather than add to it, so without
 * this a new condition would quietly resend replies that cannot be read.
 */
function requestOptions<Options>(extraOptions: Options): Options {
  const options: { retryCondition?: RetryCondition } = { ...(extraOptions as object) };
  const endpointCondition = options.retryCondition;
  if (endpointCondition) {
    options.retryCondition = (error, args, extraArgs) =>
      !isUnreadableReply(error) && endpointCondition(error, args, extraArgs);
  }
  return options as Options;
}

/**
 * Wraps a base query in RTK Query's `retry` with the app's retry rule: a
 * failed request is sent again up to `maxRetries` times with RTK's
 * exponential backoff, except a reply that arrived and cannot be read, which
 * is final (see `isUnreadableReply`). Pass `retryUnlessClientError` as `rule`
 * to also show a 4xx at once. An error the base query throws is returned
 * rather than thrown, so it, too, is retried only up to `maxRetries`.
 *
 * An endpoint can narrow the rule with its own `extraOptions.maxRetries`, or
 * replace it with `extraOptions.retryCondition`; an unreadable reply is still
 * never resent. Each error returned records how many attempts it took
 * (`attemptsMade`).
 */
export function withRetries<BaseQuery extends FetchErrorBaseQuery>(
  baseQuery: BaseQuery,
  maxRetries = DEFAULT_MAX_RETRIES,
  rule: RetryRule = retryUnlessUnreadable,
) {
  const attempts = new WeakMap<object, number>();
  const retrying = retry(eachAttempt(baseQuery, attempts), {
    retryCondition: retryRateLimited(rule(maxRetries), maxRetries),
  });
  const request: typeof retrying = async (args, api, extraOptions) => {
    const options = requestOptions(extraOptions);
    const result = await retrying(args, api, options);
    if (typeof result.error === "object" && result.error !== null) {
      attemptsByError.set(result.error, attempts.get(options) ?? 1);
    }
    return result;
  };
  return request;
}

/**
 * The base query a LiftCloud API is built on: the FlatBuffer base query for
 * `baseUrl`, retried per `withRetries`. Build a new API's base query with this
 * rather than calling `retry` directly, so it gets the same rule.
 */
export function createRetryingBaseQuery(
  baseUrl: string,
  maxRetries = DEFAULT_MAX_RETRIES,
  rule: RetryRule = retryUnlessUnreadable,
) {
  return withRetries(createFlatBufferBaseQuery(baseUrl), maxRetries, rule);
}
